import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "./AuthProvider";
import {
  getUser,
  patchUser,
  createCalculation,
  updateCalculation,
  isApiConfigured,
} from "../utils/api";
import type { CalculationType } from "../utils/api";
import "./MonthlyFinances.css";

// ─── Types ────────────────────────────────────────────────────────────────────

type LineItem = {
  description: string;
  amount: string;
  type: "personal" | "shared";
  splits: string[]; // splits[i] = person i's share %; used when type === "shared"
};

type Person = {
  name: string;
  netIncome: string;
  items: LineItem[];
};

export type SavedMonthlyFinances = {
  numPeople: number;
  people: Person[];
};

interface MonthlyFinancesProps {
  onOpenAuthForSave?: (callback: () => void) => void;
  onOpenAuth?: () => void;
  pendingRestore?: {
    type: CalculationType;
    payload: Record<string, unknown>;
    calculationId?: string;
    name?: string;
    createdAt?: string;
  } | null;
  onClearPendingRestore?: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function equalSplits(n: number): string[] {
  const base = Math.floor(100 / n);
  const rem = 100 - base * n;
  return Array.from({ length: n }, (_, i) => String(i < rem ? base + 1 : base));
}

function defaultItem(n: number): LineItem {
  return {
    description: "",
    amount: "",
    type: "personal",
    splits: equalSplits(n),
  };
}

function defaultPerson(n: number): Person {
  return { name: "", netIncome: "", items: [defaultItem(n)] };
}

type SettlementTx = { from: string; to: string; amount: number };

function calculateSettlement(
  balances: { name: string; balance: number }[],
): SettlementTx[] {
  const creds = balances
    .filter((b) => b.balance > 0.005)
    .map((b) => ({ ...b }))
    .sort((a, b) => b.balance - a.balance);
  const debts = balances
    .filter((b) => b.balance < -0.005)
    .map((b) => ({ ...b }))
    .sort((a, b) => a.balance - b.balance);
  const txns: SettlementTx[] = [];
  let i = 0;
  let j = 0;
  while (i < debts.length && j < creds.length) {
    const debt = Math.abs(debts[i].balance);
    const credit = creds[j].balance;
    const amount = Math.min(debt, credit);
    txns.push({ from: debts[i].name, to: creds[j].name, amount });
    debts[i].balance += amount;
    creds[j].balance -= amount;
    if (Math.abs(debts[i].balance) < 0.005) i++;
    if (creds[j].balance < 0.005) j++;
  }
  return txns;
}

// ─── Component ────────────────────────────────────────────────────────────────

const MonthlyFinances: React.FC<MonthlyFinancesProps> = ({
  onOpenAuthForSave,
  onOpenAuth,
  pendingRestore,
  onClearPendingRestore,
}) => {
  const { user } = useAuth();
  const [numPeople, setNumPeople] = useState<number>(2);
  const [people, setPeople] = useState<Person[]>([
    defaultPerson(2),
    defaultPerson(2),
  ]);
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadedCalculationId, setLoadedCalculationId] = useState<string | null>(null);
  const [loadedName, setLoadedName] = useState<string | null>(null);
  const [loadedCreatedAt, setLoadedCreatedAt] = useState<string | null>(null);

  const formatLoadedDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });

  const canSaveAsUser = Boolean(user && isApiConfigured());

  // ── Load / restore ──────────────────────────────────────────────────────────

  const hydrateFromSaved = (s: SavedMonthlyFinances) => {
    if (!s || typeof s.numPeople !== "number") return;
    setNumPeople(s.numPeople);
    if (Array.isArray(s.people)) setPeople(s.people as Person[]);
  };

  useEffect(() => {
    if (!user || !isApiConfigured() || loadedOnce) return;
    setLoadedOnce(true);
    getUser()
      .then((data) => {
        const s = data.monthlyFinances as SavedMonthlyFinances | undefined;
        if (s) hydrateFromSaved(s);
      })
      .catch(() => setLoadedOnce(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loadedOnce]);

  useEffect(() => {
    if (!pendingRestore || !onClearPendingRestore) return;
    if (pendingRestore.type !== "monthlyFinances") return;
    setLoadedOnce(true);
    if (pendingRestore.calculationId) setLoadedCalculationId(pendingRestore.calculationId);
    if (pendingRestore.name) setLoadedName(pendingRestore.name);
    if (pendingRestore.createdAt) setLoadedCreatedAt(pendingRestore.createdAt);
    hydrateFromSaved(pendingRestore.payload as unknown as SavedMonthlyFinances);
    onClearPendingRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRestore, onClearPendingRestore]);

  // ── PDF export (programmatic HTML from data) ─────────────────────────────────

  const reportRef = useRef<HTMLDivElement>(null);

  const exportToPDF = () => {
    const fmt = (n: number) =>
      "R\u00a0" +
      n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const reportDate = new Date().toLocaleDateString("en-ZA", {
      day: "2-digit", month: "short", year: "numeric",
    });
    const titleLine = loadedName
      ? `${loadedName} – ${loadedCreatedAt ? formatLoadedDate(loadedCreatedAt) : reportDate}`
      : `Monthly Finances – ${reportDate}`;

    // ── Solo report ──────────────────────────────────────────────────────────
    const buildSoloReport = () => {
      const person = people[0];
      const income = netIncomes[0];
      const total = person.items.reduce((s, i) => s + parseAmount(i.amount), 0);
      const rem = income - total;
      const validItems = person.items.filter((i) => parseAmount(i.amount) !== 0);

      const summaryCards = [
        income > 0 ? `<div class="card"><div class="card-label">Net Monthly Income</div><div class="card-value">${fmt(income)}</div></div>` : "",
        `<div class="card"><div class="card-label">Total expenses</div><div class="card-value">${fmt(total)}</div></div>`,
        income > 0 ? `<div class="card ${rem >= 0 ? "card-green" : "card-red"}"><div class="card-label">Remaining</div><div class="card-value">${fmt(rem)}</div><div class="card-sub">${rem >= 0 ? "Under budget" : "Over budget"}</div></div>` : "",
      ].filter(Boolean).join("");

      const itemRows = validItems.map(item =>
        `<tr><td>${item.description || "—"}</td><td class="td-right">${fmt(parseAmount(item.amount))}</td></tr>`
      ).join("");

      return `
        <div class="summary-grid">${summaryCards}</div>
        ${validItems.length > 0 ? `
        <h2>Expense breakdown</h2>
        <table>
          <thead><tr><th>Description</th><th class="td-right">Amount</th></tr></thead>
          <tbody>${itemRows}</tbody>
          <tfoot><tr><td><strong>Total</strong></td><td class="td-right"><strong>${fmt(total)}</strong></td></tr></tfoot>
        </table>` : ""}
      `;
    };

    // ── Multi-person report ──────────────────────────────────────────────────
    const buildMultiReport = () => {
      // Per-person summary cards
      const personCards = people.map((_, i) => {
        const totalOut = sharedResponsibility[i] + personalTotal[i];
        const hasIncome = netIncomes[i] > 0;
        const remaining = netIncomes[i] - totalOut;
        return `
          <div class="card">
            <div class="card-label">${displayName(i)}</div>
            ${hasIncome ? `<div class="card-row"><span>Income</span><strong>${fmt(netIncomes[i])}</strong></div>` : ""}
            ${totalShared > 0 ? `<div class="card-row"><span>Shared share</span><strong>${fmt(sharedResponsibility[i])}</strong></div>` : ""}
            ${personalTotal[i] > 0 ? `<div class="card-row"><span>Personal</span><strong>${fmt(personalTotal[i])}</strong></div>` : ""}
            <div class="card-row card-row-total"><span>Total outgoings</span><strong>${fmt(totalOut)}</strong></div>
            ${hasIncome ? `<div class="card-row ${remaining >= 0 ? "card-row-green" : "card-row-red"}"><span>Remaining</span><strong>${fmt(remaining)}</strong></div>` : ""}
          </div>`;
      }).join("");

      // Settlement
      const settlementHtml = totalShared > 0 ? `
        <h2>Settlement</h2>
        <p class="hint">Based on shared expenses only — personal expenses are not included.</p>
        ${allSettled
          ? `<div class="settled">All square! No money needs to change hands.</div>`
          : transactions.map(tx => `
              <div class="settlement-item">
                <span class="from">${tx.from}</span>
                <span class="arrow">→</span>
                <span class="to">${tx.to}</span>
                <span class="amount">${fmt(tx.amount)}</span>
              </div>`).join("")
        }` : "";

      // Shared breakdown table
      const sharedItems = allSharedItems.filter(item => parseAmount(item.amount) !== 0);
      const sharedBreakdown = sharedItems.length > 0 ? `
        <h2>Shared expense breakdown</h2>
        <table>
          <thead>
            <tr>
              <th>Description</th><th>Paid by</th><th class="td-right">Amount</th>
              ${people.map((_, i) => `<th class="td-right">${personShareHeading(i)}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${sharedItems.map(item => {
              const amt = parseAmount(item.amount);
              return `<tr>
                <td>${item.description || "—"}</td>
                <td>${displayName(item.paidByIdx)}</td>
                <td class="td-right">${fmt(amt)}</td>
                ${people.map((_, i) => `<td class="td-right">${fmt(amt * (parsePercent(item.splits[i] ?? "0") / 100))}</td>`).join("")}
              </tr>`;
            }).join("")}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td><td></td><td class="td-right"><strong>${fmt(totalShared)}</strong></td>
              ${people.map((_, i) => `<td class="td-right"><strong>${fmt(sharedResponsibility[i])}</strong></td>`).join("")}
            </tr>
          </tfoot>
        </table>` : "";

      // Per-person breakdowns
      const perPersonBreakdowns = people.map((person, personIdx) => {
        const validItems = person.items.filter(item => parseAmount(item.amount) !== 0);
        if (validItems.length === 0) return "";
        const personTotal = validItems.reduce((s, item) => s + parseAmount(item.amount), 0);
        return `
          <h2>${personExpensesSectionTitle(personIdx)}</h2>
          <table>
            <thead><tr><th>Description</th><th>Type</th><th class="td-right">Amount</th></tr></thead>
            <tbody>
              ${validItems.map(item => `
                <tr>
                  <td>${item.description || "—"}</td>
                  <td class="${item.type === "shared" ? "badge-shared" : "badge-personal"}">${item.type === "shared" ? "Shared" : "Personal"}</td>
                  <td class="td-right">${fmt(parseAmount(item.amount))}</td>
                </tr>`).join("")}
            </tbody>
            <tfoot>
              <tr><td><strong>Total</strong></td><td></td><td class="td-right"><strong>${fmt(personTotal)}</strong></td></tr>
            </tfoot>
          </table>`;
      }).join("");

      return `
        <h2>Summary per Person</h2>
        <div class="summary-grid">${personCards}</div>
        ${settlementHtml}
        ${sharedBreakdown}
        ${perPersonBreakdowns}
      `;
    };

    const reportContent = solo ? buildSoloReport() : buildMultiReport();

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${titleLine}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; font-size: 13px; color: #111; line-height: 1.5; padding: 32px; }
    h1 { font-size: 20px; font-weight: 700; margin-bottom: 2px; }
    .report-date { font-size: 12px; color: #666; margin-bottom: 28px; }
    h2 { font-size: 14px; font-weight: 700; margin: 24px 0 10px; padding-bottom: 5px; border-bottom: 1.5px solid #e5e7eb; color: #333; text-transform: uppercase; letter-spacing: 0.04em; }
    .hint { font-size: 11px; color: #888; margin-bottom: 8px; }
    .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-top: 4px; }
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 16px; background: #fafafa; }
    .card-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; font-weight: 600; }
    .card-value { font-size: 18px; font-weight: 700; color: #111; }
    .card-sub { font-size: 11px; color: #888; margin-top: 2px; }
    .card-green { border-color: #16a34a; background: #f0fdf4; }
    .card-green .card-value { color: #16a34a; }
    .card-red { border-color: #dc2626; background: #fef2f2; }
    .card-red .card-value { color: #dc2626; }
    .card-row { display: flex; justify-content: space-between; font-size: 12px; padding: 2px 0; color: #444; }
    .card-row-total { border-top: 1px solid #e5e7eb; margin-top: 4px; padding-top: 4px; font-weight: 600; }
    .card-row-green { color: #16a34a; font-weight: 600; }
    .card-row-red { color: #dc2626; font-weight: 600; }
    .settled { display: inline-block; background: #f0fdf4; border: 1px solid #16a34a; color: #16a34a; border-radius: 6px; padding: 8px 16px; font-weight: 600; font-size: 13px; }
    .settlement-item { display: flex; align-items: center; gap: 10px; padding: 8px 14px; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 6px; font-size: 13px; background: #fafafa; }
    .from, .to { font-weight: 600; }
    .arrow { color: #9ca3af; }
    .amount { margin-left: auto; font-weight: 700; color: #16a34a; }
    table { width: 100%; border-collapse: collapse; margin-top: 4px; font-size: 12px; }
    thead tr { background: #f5f5f5; }
    th { text-align: left; padding: 8px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #666; font-weight: 700; border-bottom: 1.5px solid #e5e7eb; }
    td { padding: 8px 12px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
    tbody tr:nth-child(even) { background: #fafafa; }
    tfoot td { font-weight: 700; background: #f0f0f0; border-top: 1.5px solid #ddd; }
    .td-right { text-align: right; white-space: nowrap; }
    .badge-shared { color: #6366f1; font-weight: 600; }
    .badge-personal { color: #9ca3af; }
    @page { margin: 14mm 16mm; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>${solo ? (people[0].name ? people[0].name + "'s" : "Your") + " Monthly Finances" : "Monthly Finances"}</h1>
  <div class="report-date">${titleLine}</div>
  ${reportContent}
</body>
</html>`;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;width:0;height:0;border:0;opacity:0;";
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument!;
    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow!.focus();
      iframe.contentWindow!.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1000);
    }, 250);
  };

  // ── Save ────────────────────────────────────────────────────────────────────

  const handleSaveClick = (forceNew = false) => {
    if (canSaveAsUser) {
      handleSave(forceNew);
    } else if (!user) {
      if (onOpenAuthForSave) {
        onOpenAuthForSave(() => handleSave(forceNew));
      } else {
        onOpenAuth?.();
      }
    }
  };

  const handleSave = async (forceNew = false) => {
    if (!canSaveAsUser) return;
    setSaveStatus("saving");
    const payload: SavedMonthlyFinances = { numPeople, people };
    try {
      if (loadedCalculationId && !forceNew) {
        await updateCalculation(loadedCalculationId, { payload: payload as unknown as Record<string, unknown> });
      } else {
        const created = await createCalculation({
          calculationType: "monthlyFinances",
          payload: payload as unknown as Record<string, unknown>,
        });
        setLoadedCalculationId(created.calculationId);
        setLoadedName(created.name ?? null);
        setLoadedCreatedAt(created.createdAt ?? null);
      }
      await patchUser({ monthlyFinances: payload }).catch(() => {});
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  // ── People count ────────────────────────────────────────────────────────────

  const handleNumPeopleChange = (n: number) => {
    setNumPeople(n);
    setPeople((prev) => {
      const next = [...prev];
      while (next.length < n) next.push(defaultPerson(n));
      while (next.length > n) next.pop();
      return next.map((p) => ({
        ...p,
        items: p.items.map((item) => ({
          ...item,
          splits: item.type === "shared" ? equalSplits(n) : item.splits,
        })),
      }));
    });
  };

  // ── Person field ────────────────────────────────────────────────────────────

  const updatePersonField = (
    pIdx: number,
    field: "name" | "netIncome",
    value: string,
  ) => {
    setPeople((prev) =>
      prev.map((p, i) => (i === pIdx ? { ...p, [field]: value } : p)),
    );
  };

  // ── Item updates ────────────────────────────────────────────────────────────

  const updateItemField = (
    pIdx: number,
    iIdx: number,
    field: "description" | "amount" | "type",
    value: string,
  ) => {
    setPeople((prev) =>
      prev.map((p, i) =>
        i === pIdx
          ? {
              ...p,
              items: p.items.map((item, j) =>
                j === iIdx
                  ? {
                      ...item,
                      [field]: value,
                      // Initialise equal splits when switching to shared
                      ...(field === "type" &&
                        value === "shared" && {
                          splits: equalSplits(numPeople),
                        }),
                    }
                  : item,
              ),
            }
          : p,
      ),
    );
  };

  const updateItemSplit = (
    pIdx: number,
    iIdx: number,
    sIdx: number,
    value: string,
  ) => {
    setPeople((prev) =>
      prev.map((p, i) =>
        i === pIdx
          ? {
              ...p,
              items: p.items.map((item, j) =>
                j === iIdx
                  ? {
                      ...item,
                      splits: item.splits.map((s, k) =>
                        k === sIdx ? value : s,
                      ),
                    }
                  : item,
              ),
            }
          : p,
      ),
    );
  };

  const addItem = (pIdx: number) => {
    setPeople((prev) =>
      prev.map((p, i) =>
        i === pIdx ? { ...p, items: [...p.items, defaultItem(numPeople)] } : p,
      ),
    );
  };

  const removeItem = (pIdx: number, iIdx: number) => {
    setPeople((prev) =>
      prev.map((p, i) =>
        i === pIdx && p.items.length > 1
          ? { ...p, items: p.items.filter((_, j) => j !== iIdx) }
          : p,
      ),
    );
  };

  // ── Calculations ────────────────────────────────────────────────────────────

  const parseAmount = (s: string) => parseFloat(s.replace(/[,\s]/g, "")) || 0;
  const parsePercent = (s: string) => {
    const v = parseFloat(s);
    return isNaN(v) ? 0 : Math.min(100, Math.max(0, v));
  };
  const formatCurrency = (n: number) =>
    new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  const allSharedItems = people.flatMap((p, pIdx) =>
    p.items
      .filter((item) => item.type === "shared")
      .map((item) => ({ ...item, paidByIdx: pIdx })),
  );

  const totalShared = allSharedItems.reduce(
    (sum, item) => sum + parseAmount(item.amount),
    0,
  );

  const sharedResponsibility = people.map((_, i) =>
    allSharedItems.reduce(
      (sum, item) =>
        sum +
        parseAmount(item.amount) * (parsePercent(item.splits[i] ?? "0") / 100),
      0,
    ),
  );

  const paidSharedByPerson = people.map((p) =>
    p.items
      .filter((item) => item.type === "shared")
      .reduce((sum, item) => sum + parseAmount(item.amount), 0),
  );

  const personalTotal = people.map((p) =>
    p.items
      .filter((item) => item.type === "personal")
      .reduce((sum, item) => sum + parseAmount(item.amount), 0),
  );

  const netIncomes = people.map((p) => parseAmount(p.netIncome));
  const balances = people.map(
    (_, i) => paidSharedByPerson[i] - sharedResponsibility[i],
  );

  const displayName = (i: number) => {
    if (numPeople === 1) return people[0].name || "You";
    if (i === 0) return people[0].name || "You";
    return people[i].name || `Person ${i + 1}`;
  };

  const transactions = calculateSettlement(
    people.map((_, i) => ({ name: displayName(i), balance: balances[i] })),
  );
  const allSettled = transactions.length === 0;

  const hasAnyValidInput = people.some(
    (p) =>
      parseAmount(p.netIncome) > 0 ||
      p.items.some((item) => parseAmount(item.amount) !== 0),
  );

  const personNameLabel = (i: number) => {
    if (numPeople === 1) return "Your name";
    if (i === 0) return "Your name";
    if (numPeople === 2) return "Their name";
    return `Name ${i + 1}`;
  };

  const personNamePlaceholder = (i: number) => {
    if (i === 0) return "e.g., Sam";
    if (numPeople === 2) return "e.g., Jamie";
    return `e.g., Person ${i + 1}`;
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const solo = numPeople === 1;

  /** Section headings — avoid "You's expenses"; use "Your expenses" for person 1 when unnamed */
  const personExpensesSectionTitle = (i: number): string => {
    if (solo) return "Your expenses";
    const raw = people[i].name?.trim();
    if (i === 0 && (!raw || raw.toLowerCase() === "you")) return "Your expenses";
    const label = raw || `Person ${i + 1}`;
    return `${label}'s expenses`;
  };

  const personShareHeading = (i: number): string => {
    if (solo) return "Your share";
    const raw = people[i].name?.trim();
    if (i === 0 && (!raw || raw.toLowerCase() === "you")) return "Your share";
    const label = raw || `Person ${i + 1}`;
    return `${label}'s share`;
  };

  const renderItemRow = (pIdx: number, iIdx: number, item: LineItem) => {
    const isShared = !solo && item.type === "shared";
    const splitSum = item.splits.reduce((s, v) => s + parsePercent(v), 0);
    const splitValid = Math.abs(splitSum - 100) < 1;

    return (
      <div key={iIdx} className="mf-item-row">
        <div className="mf-col-desc">
          <input
            type="text"
            value={item.description}
            onChange={(e) =>
              updateItemField(pIdx, iIdx, "description", e.target.value)
            }
            placeholder="e.g., Groceries"
            aria-label={`Item ${iIdx + 1} description`}
          />
        </div>
        <div className="mf-col-amount">
          <input
            type="text"
            value={item.amount}
            onChange={(e) =>
              updateItemField(pIdx, iIdx, "amount", e.target.value)
            }
            placeholder="e.g., 2 500"
            aria-label={`Item ${iIdx + 1} amount`}
          />
        </div>

        {/* Type dropdown — multi-person only */}
        {!solo && (
          <div className="mf-col-type">
            <select
              value={item.type}
              onChange={(e) =>
                updateItemField(
                  pIdx,
                  iIdx,
                  "type",
                  e.target.value as "shared" | "personal",
                )
              }
              aria-label={`Item ${iIdx + 1} type`}
            >
              <option value="personal">Personal</option>
              <option value="shared">Shared</option>
            </select>
          </div>
        )}

        {/* Split inputs — always shown in multi-person; disabled with "0% shared" when personal */}
        {!solo && (
          <div className="mf-col-split">
            {isShared ? (
              numPeople === 2 ? (
                <div className="mf-split-two">
                  <input
                    type="number"
                    value={item.splits[0]}
                    onChange={(e) =>
                      updateItemSplit(pIdx, iIdx, 0, e.target.value)
                    }
                    min="0"
                    max="100"
                    step="1"
                    aria-label={`${personShareHeading(0)} %`}
                  />
                  <span className="mf-split-badge">
                    / {100 - parsePercent(item.splits[0])}%
                  </span>
                </div>
              ) : (
                <div className="mf-split-multi">
                  {item.splits.map((split, sIdx) => (
                    <div key={sIdx} className="mf-split-cell">
                      <span className="mf-split-person-label">
                        {(people[sIdx]?.name || `P${sIdx + 1}`)
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                      <input
                        type="number"
                        value={split}
                        onChange={(e) =>
                          updateItemSplit(pIdx, iIdx, sIdx, e.target.value)
                        }
                        min="0"
                        max="100"
                        step="1"
                        aria-label={`${personShareHeading(sIdx)} %`}
                      />
                    </div>
                  ))}
                  <span
                    className={`mf-split-sum ${
                      splitValid ? "mf-split-sum--ok" : "mf-split-sum--warn"
                    }`}
                  >
                    = {splitSum}%
                  </span>
                </div>
              )
            ) : (
              <div className="mf-split-personal">
                <input
                  type="text"
                  value="0% shared"
                  disabled
                  aria-label="Not shared"
                />
              </div>
            )}
          </div>
        )}

        <div className="mf-col-remove">
          {people[pIdx].items.length > 1 && (
            <button
              type="button"
              className="mf-remove-btn"
              onClick={() => removeItem(pIdx, iIdx)}
              aria-label={`Remove item ${iIdx + 1}`}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── JSX ─────────────────────────────────────────────────────────────────────

  return (
    <div className="monthly-finances">
      <div className="calculator-container">
        <div className="calculator-title-row">
          <div>
            <h1>Monthly Finances</h1>
            {loadedCalculationId && loadedName ? (
              <p className="loaded-file-label">
                {loadedName}{loadedCreatedAt ? ` – ${formatLoadedDate(loadedCreatedAt)}` : ""}
              </p>
            ) : (
              <p className="subtitle">
                {solo
                  ? "Track your monthly income and expenses — know exactly where your money goes."
                  : "Track shared and personal expenses, split costs fairly, and see exactly who owes who."}
              </p>
            )}
          </div>
        </div>

        {/* ── How many people ──────────────────────────────────────────── */}
        <div className="calculator-section">
          <h2 className="mf-people-count-heading">How many people?</h2>
          <div className="calculator-tabs">
            {([1, 2, 3, 4] as const).map((n) => (
              <button
                key={n}
                type="button"
                className={`tab-button ${numPeople === n ? "active" : ""}`}
                onClick={() => handleNumPeopleChange(n)}
              >
                {n === 1 ? "Just me" : `${n} people`}
              </button>
            ))}
          </div>
        </div>

        {/* ── People details ───────────────────────────────────────────── */}
        <div className={`mf-people-grid mf-people-grid--${numPeople}`}>
          {people.map((person, i) => (
            <div key={i} className="mf-person-card">
              <div className="input-group">
                <label htmlFor={`person-name-${i}`}>{personNameLabel(i)}</label>
                <input
                  id={`person-name-${i}`}
                  type="text"
                  value={person.name}
                  onChange={(e) => updatePersonField(i, "name", e.target.value)}
                  placeholder={personNamePlaceholder(i)}
                />
              </div>
              <div className="input-group">
                <label htmlFor={`person-income-${i}`}>
                  {solo || i === 0 ? "Your" : "Their"} net monthly income{" "}
                  <span className="mf-optional">(optional)</span>
                </label>
                <input
                  id={`person-income-${i}`}
                  type="text"
                  value={person.netIncome}
                  onChange={(e) =>
                    updatePersonField(i, "netIncome", e.target.value)
                  }
                  placeholder="e.g., 30 000"
                />
              </div>
            </div>
          ))}
        </div>

        {/* ── Expense sections per person ──────────────────────────────── */}
        {people.map((person, pIdx) => (
          <div key={pIdx} className="calculator-section">
            <h2>{personExpensesSectionTitle(pIdx)}</h2>
            {!solo && (
              <p className="input-hint">
                Mark an item as <strong>Shared</strong> to split it with others
                — the split % field will appear. Use a negative amount for
                rebates.
              </p>
            )}

            <div className="mf-item-header" aria-hidden="true">
              <span className="mf-col-desc">Description</span>
              <span className="mf-col-amount">Amount (R)</span>
              {!solo && <span className="mf-col-type">Type</span>}
              {!solo && <span className="mf-col-split">Split (%)</span>}
              <span className="mf-col-remove" />
            </div>

            {person.items.map((item, iIdx) => renderItemRow(pIdx, iIdx, item))}

            <button
              type="button"
              className="mf-add-btn"
              onClick={() => addItem(pIdx)}
            >
              + Add expense
            </button>
          </div>
        ))}

        {/* ── Results ─────────────────────────────────────────────────── */}
        {hasAnyValidInput && (
          <div className="export-button-container">
            <button className="export-pdf-button" onClick={exportToPDF}>
              📄 Export to PDF
            </button>
          </div>
        )}
        {hasAnyValidInput && (
          <div className="results-section" ref={reportRef}>
            {solo ? (
              <>
                <h2>Monthly Summary</h2>
                <div className="result-grid">
                  {netIncomes[0] > 0 && (
                    <div className="result-card">
                      <h3>Net Monthly Income</h3>
                      <p className="result-value">
                        {formatCurrency(netIncomes[0])}
                      </p>
                    </div>
                  )}
                  <div className="result-card">
                    <h3>Total expenses</h3>
                    <p className="result-value">
                      {formatCurrency(
                        people[0].items.reduce(
                          (s, item) => s + parseAmount(item.amount),
                          0,
                        ),
                      )}
                    </p>
                  </div>
                  {netIncomes[0] > 0 &&
                    (() => {
                      const total = people[0].items.reduce(
                        (s, i) => s + parseAmount(i.amount),
                        0,
                      );
                      const rem = netIncomes[0] - total;
                      return (
                        <div
                          className={`result-card ${rem >= 0 ? "affordability-green" : "affordability-red"}`}
                        >
                          <h3>Remaining</h3>
                          <p className="result-value">{formatCurrency(rem)}</p>
                          <p className="result-detail">
                            {rem >= 0 ? "Under budget" : "Over budget"}
                          </p>
                        </div>
                      );
                    })()}
                </div>

                {people[0].items.some(
                  (item) => parseAmount(item.amount) !== 0,
                ) && (
                  <>
                    <h2>Expense breakdown</h2>
                    <div className="mf-breakdown-table">
                      <div className="mf-breakdown-header mf-breakdown-header--solo">
                        <span>Description</span>
                        <span>Amount</span>
                      </div>
                      {people[0].items
                        .filter((item) => parseAmount(item.amount) !== 0)
                        .map((item, i) => (
                          <div
                            key={i}
                            className="mf-breakdown-row mf-breakdown-row--solo"
                          >
                            <span className="mf-breakdown-desc">
                              {item.description || "—"}
                            </span>
                            <span>
                              {formatCurrency(parseAmount(item.amount))}
                            </span>
                          </div>
                        ))}
                      <div className="mf-breakdown-total mf-breakdown-total--solo">
                        <span>Total</span>
                        <span>
                          {formatCurrency(
                            people[0].items.reduce(
                              (s, i) => s + parseAmount(i.amount),
                              0,
                            ),
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {/* Per-person summary */}
                <h2>Summary per Person</h2>
                <div className="result-grid">
                  {people.map((_, i) => {
                    const totalOut = sharedResponsibility[i] + personalTotal[i];
                    const hasIncome = netIncomes[i] > 0;
                    const remaining = netIncomes[i] - totalOut;
                    return (
                      <div
                        key={i}
                        className="result-card mf-person-summary-card"
                      >
                        <h3>{displayName(i)}</h3>
                        {hasIncome && (
                          <div className="mf-summary-row">
                            <span>Income</span>
                            <strong>{formatCurrency(netIncomes[i])}</strong>
                          </div>
                        )}
                        {totalShared > 0 && (
                          <div className="mf-summary-row">
                            <span>Shared share</span>
                            <strong>
                              {formatCurrency(sharedResponsibility[i])}
                            </strong>
                          </div>
                        )}
                        {personalTotal[i] > 0 && (
                          <div className="mf-summary-row">
                            <span>Personal</span>
                            <strong>{formatCurrency(personalTotal[i])}</strong>
                          </div>
                        )}
                        <div className="mf-summary-row mf-summary-row--total">
                          <span>Total outgoings</span>
                          <strong>{formatCurrency(totalOut)}</strong>
                        </div>
                        {hasIncome && (
                          <div
                            className={`mf-summary-row mf-summary-remaining ${
                              remaining >= 0
                                ? "mf-remaining--positive"
                                : "mf-remaining--negative"
                            }`}
                          >
                            <span>Remaining</span>
                            <strong>{formatCurrency(remaining)}</strong>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Settlement */}
                {totalShared > 0 && (
                  <>
                    <h2>Settlement</h2>
                    <p className="input-hint">
                      Based on shared expenses only — personal expenses are not
                      included.
                    </p>
                    {allSettled ? (
                      <div className="result-card affordability-green mf-settled-card">
                        <p className="result-value">All square!</p>
                        <p className="result-detail">
                          No money needs to change hands.
                        </p>
                      </div>
                    ) : (
                      <div className="mf-settlement-list">
                        {transactions.map((tx, i) => (
                          <div key={i} className="mf-settlement-item">
                            <span className="mf-settlement-from">
                              {tx.from}
                            </span>
                            <span className="mf-settlement-arrow">→</span>
                            <span className="mf-settlement-to">{tx.to}</span>
                            <span className="mf-settlement-amount">
                              {formatCurrency(tx.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Shared breakdown table */}
                {allSharedItems.some(
                  (item) => parseAmount(item.amount) !== 0,
                ) && (
                  <>
                    <h2>Shared expense breakdown</h2>
                    <div className="mf-breakdown-table">
                      <div
                        className={`mf-breakdown-header mf-breakdown-header--${numPeople}p`}
                      >
                        <span>Description</span>
                        <span>Paid by</span>
                        <span>Amount</span>
                        {people.map((_, i) => (
                          <span key={i}>{personShareHeading(i)}</span>
                        ))}
                      </div>
                      {allSharedItems
                        .filter((item) => parseAmount(item.amount) !== 0)
                        .map((item, idx) => {
                          const amt = parseAmount(item.amount);
                          return (
                            <div
                              key={idx}
                              className={`mf-breakdown-row mf-breakdown-row--${numPeople}p`}
                            >
                              <span className="mf-breakdown-desc">
                                {item.description || "—"}
                              </span>
                              <span>{displayName(item.paidByIdx)}</span>
                              <span>{formatCurrency(amt)}</span>
                              {people.map((_, i) => (
                                <span key={i}>
                                  {formatCurrency(
                                    amt *
                                      (parsePercent(item.splits[i] ?? "0") /
                                        100),
                                  )}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                      <div
                        className={`mf-breakdown-total mf-breakdown-total--${numPeople}p`}
                      >
                        <span>Total</span>
                        <span />
                        <span>{formatCurrency(totalShared)}</span>
                        {people.map((_, i) => (
                          <span key={i}>
                            {formatCurrency(sharedResponsibility[i])}
                          </span>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Per-person expense breakdown */}
                {people.map((person, personIdx) => {
                  const personItems = person.items.filter(
                    (item) => parseAmount(item.amount) !== 0,
                  );
                  if (personItems.length === 0) return null;
                  const personTotal = personItems.reduce(
                    (s, item) => s + parseAmount(item.amount),
                    0,
                  );
                  return (
                    <React.Fragment key={personIdx}>
                      <h2>{personExpensesSectionTitle(personIdx)}</h2>
                      <div className="mf-breakdown-table">
                        <div className="mf-breakdown-header mf-breakdown-header--3col">
                          <span>Description</span>
                          <span>Type</span>
                          <span>Amount</span>
                        </div>
                        {personItems.map((item, i) => (
                          <div
                            key={i}
                            className="mf-breakdown-row mf-breakdown-row--3col"
                          >
                            <span className="mf-breakdown-desc">
                              {item.description || "—"}
                            </span>
                            <span
                              className={
                                item.type === "shared"
                                  ? "mf-breakdown-type-shared"
                                  : "mf-breakdown-type-personal"
                              }
                            >
                              {item.type === "shared" ? "Shared" : "Personal"}
                            </span>
                            <span>
                              {formatCurrency(parseAmount(item.amount))}
                            </span>
                          </div>
                        ))}
                        <div className="mf-breakdown-total mf-breakdown-total--3col">
                          <span>Total</span>
                          <span />
                          <span>{formatCurrency(personTotal)}</span>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </div>
        )}

        {/* ── Save ────────────────────────────────────────────────────── */}
        <div className="calculator-save-row">
          {loadedCalculationId ? (
            <>
              <button
                type="button"
                className="save-button"
                onClick={() => handleSaveClick(false)}
                disabled={!hasAnyValidInput || saveStatus === "saving"}
              >
                {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save"}
              </button>
              <button
                type="button"
                className="save-new-button"
                onClick={() => handleSaveClick(true)}
                disabled={!hasAnyValidInput || saveStatus === "saving"}
              >
                Save new
              </button>
            </>
          ) : (
            <button
              type="button"
              className="save-button"
              onClick={() => handleSaveClick(false)}
              disabled={!hasAnyValidInput || saveStatus === "saving"}
            >
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : saveStatus === "error" ? "Try again" : "Save for later"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonthlyFinances;
